/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import EmailIcon from "@mui/icons-material/Email";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Button, Paper, Theme, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import henchChefLogo from "assets/broken-hench-chef.png";
import { hideInitialLoadingScreen } from "contexts/globalLoadingScreen";
import { motion } from "framer-motion";
import { useEffect } from "react";
import {
  FALLBACK_TEXT,
  logoutAndRedirect,
  normalizeError,
  refreshPage,
  SUPPORT_EMAIL,
} from "./errorUtils";
import { PureHtmlFallback } from "./PureHtmlFallback";
import { useSafeIsAdmin, useSafeTheme, useSafeTranslation } from "./safeHooks";

interface GlobalErrorFallbackProps {
  error?: Error;
}

export const GlobalErrorFallback = ({ error }: GlobalErrorFallbackProps) => {
  const normalizedError = normalizeError(error);

  const theme = useSafeTheme();
  const { t } = useSafeTranslation();
  const isAdmin = useSafeIsAdmin();

  useEffect(() => {
    try {
      hideInitialLoadingScreen();
    } catch (e) {
      console.error(e);
      // Error recovery remains available if the loading screen cannot be hidden.
    }
  }, []);

  if (!theme) {
    return <PureHtmlFallback error={normalizedError} />;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={<PureHtmlFallback error={normalizedError} />}
    >
      <GlobalErrorFallbackInner
        error={normalizedError}
        theme={theme}
        t={t}
        isAdmin={isAdmin}
      />
    </Sentry.ErrorBoundary>
  );
};

/** MUI error view guarded by a dependency-free fallback. */
const GlobalErrorFallbackInner = ({
  error,
  theme,
  t,
  isAdmin,
}: {
  error?: Error;
  theme: Theme;
  t: (key: string) => string;
  isAdmin: boolean;
}) => {
  const errorId = error?.name ?? "Unknown";

  const handleLogout = () => void logoutAndRedirect();

  const handleEmailClick = () => {
    const subject = encodeURIComponent(
      `[ScrapChef Error] ${errorId}: ${error?.message ?? "Application Error"}`
    );
    window.open(`mailto:${SUPPORT_EMAIL}?subject=${subject}`);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.palette.grey[50],
        zIndex: theme.zIndex.modal + 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.4,
          backgroundImage: `radial-gradient(${theme.palette.grey[200]} 1px, transparent 1px)`,
          backgroundSize: "24px 24px",
        }}
      />

      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1],
        }}
        elevation={0}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 2, sm: 3 },
          p: { xs: 3, sm: 5 },
          maxWidth: 480,
          mx: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.grey[200]}`,
          backgroundColor: theme.palette.common.white,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            borderRadius: "8px 8px 0 0",
            background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.light})`,
          }}
        />

        <Box
          sx={{
            position: "relative",
            mt: 2,
            mb: 1,
          }}
        >
          <Box
            component="img"
            src={henchChefLogo}
            alt="ScrapChef"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
            sx={{
              display: "block",
              width: { xs: 160, sm: 220 },
              height: { xs: 160, sm: 220 },
              objectFit: "contain",
              borderRadius: 3,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -6,
              right: -6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: "50%",
              backgroundColor: theme.palette.common.white,
              border: `2px solid ${theme.palette.error.light}`,
            }}
          >
            <WarningAmberRoundedIcon
              sx={{
                fontSize: { xs: 22, sm: 28 },
                color: theme.palette.error.main,
                display: "block",
                transform: "translateY(-2px)",
              }}
            />
          </Box>
        </Box>

        <Typography
          variant="h3"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: "fontWeightBold",
            textAlign: "center",
          }}
        >
          {t("globalErrorTitle")}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            textAlign: "center",
            lineHeight: 1.7,
          }}
        >
          {t("globalErrorDescription")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mt: 1,
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={refreshPage}
            fullWidth
            sx={{
              py: 1.25,
            }}
          >
            {t("globalErrorRefresh")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={handleEmailClick}
            fullWidth
            sx={{
              py: 1.25,
              borderColor: theme.palette.grey[300],
              color: theme.palette.text.primary,
              "&:hover": {
                borderColor: theme.palette.grey[400],
                backgroundColor: theme.palette.grey[50],
              },
            }}
          >
            {t("globalErrorContactSupport")}
          </Button>
        </Box>

        <Button
          variant="text"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            textTransform: "none",
            color: theme.palette.text.secondary,
          }}
        >
          {t("signOut") ?? FALLBACK_TEXT.signOut ?? "Sign Out"}
        </Button>

        {error && isAdmin ? (
          <Typography
            variant="body2Mono"
            sx={{
              color: theme.palette.grey[400],
              mt: 1,
              px: 2,
              py: 0.75,
              borderRadius: 1,
              backgroundColor: theme.palette.grey[50],
              width: "100%",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {error.name}: {error.message}
          </Typography>
        ) : null}
      </Paper>
    </Box>
  );
};
